import React from 'react';
import { Activity, AlertTriangle, Power, Wrench, Wifi, WifiOff } from 'lucide-react';

interface MachineState {
  machineId: number;
  machineNumber: string;
  D2: number;
  D5: number;
  RPM: number;
  isOnline: boolean;
  reason: 'on' | 'fault' | 'manual' | 'ignored';
  timestamp: Date;
  statusText: string;
  statusColor: string;
  statusIcon: string;
}

interface RealtimeMachineStatusProps {
  machineNumber: number;
  machineState: MachineState | null;
  isConnected: boolean;
  error?: string | null;
  showDetails?: boolean;
}

const RealtimeMachineStatus: React.FC<RealtimeMachineStatusProps> = ({
  machineNumber,
  machineState,
  isConnected,
  error,
  showDetails = false
}) => {
  const getStatusColor = (reason: string, isOnline: boolean, isStale: boolean) => {
    if (!isConnected) return 'text-gray-500 bg-gray-100';
    if (!machineState) return 'text-gray-500 bg-gray-100';
    if (isStale) return 'text-gray-500 bg-gray-100'; // Stale data shows as offline
    if (isOnline) return 'text-green-600 bg-green-100';
    if (reason === 'fault') return 'text-red-600 bg-red-100';
    if (reason === 'manual') return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-500 bg-gray-100';
  };

  const getStatusIcon = (reason: string, isOnline: boolean) => {
    if (!isConnected) return <WifiOff className="h-3 w-3" />;
    if (isOnline || reason === 'ignored') return <Activity className="h-3 w-3" />;
    if (reason === 'fault') return <AlertTriangle className="h-3 w-3" />;
    if (reason === 'manual') return <Wrench className="h-3 w-3" />;
    return <Power className="h-3 w-3" />;
  };

  const getStatusText = (reason: string, isOnline: boolean, isStale: boolean) => {
    if (!isConnected) return 'Disconnected';
    if (!machineState) return 'No Data';
    if (isStale) return 'Offline'; // Stale data shows as offline
    if (isOnline) return 'Running';
    if (reason === 'fault') return 'Fault';
    if (reason === 'manual') return 'Manual Stop';
    if (reason === 'ignored') return 'Offline';
    return 'Unknown';
  };

  const getConnectionStatus = () => {
    if (error) {
      return (
        <div className="flex items-center space-x-1 text-red-600">
          <WifiOff className="h-3 w-3" />
          <span className="text-xs">Error</span>
        </div>
      );
    }
    
    // Show WebSocket connection status (not machine status)
    // "Live" means WebSocket is connected and receiving data
    // "Offline" means WebSocket is disconnected or no data received
    if (!isConnected) {
      return (
        <div className="flex items-center space-x-1 text-gray-500">
          <WifiOff className="h-3 w-3" />
          <span className="text-xs">Offline</span>
        </div>
      );
    }
    
    // If connected but no machine state, show "Connecting"
    if (!machineState) {
      return (
        <div className="flex items-center space-x-1 text-yellow-600">
          <Wifi className="h-3 w-3" />
          <span className="text-xs">Connecting</span>
        </div>
      );
    }
    
    // If connected and have machine state, show "Live"
    return (
      <div className="flex items-center space-x-1 text-green-600">
        <Wifi className="h-3 w-3" />
        <span className="text-xs">Live</span>
      </div>
    );
  };

  if (!machineState && !error) {
    return (
      <div className="flex items-center space-x-2">
        {/* Only show connection status when no machine state */}
        {getConnectionStatus()}
      </div>
    );
  }

  // Check if machine is offline due to no update in past 1 minute (ONLY IN FRONTEND)
  const now = Date.now();
  const lastUpdateTime = machineState?.timestamp ? new Date(machineState.timestamp).getTime() : 0;
  const isStale = machineState ? (lastUpdateTime > 0 && (now - lastUpdateTime) > 60000) : false; // 1 minute = 60000ms
  
  // Get status values with proper fallbacks
  const reason = machineState?.reason || 'ignored';
  const isOnline = machineState?.isOnline || false;
  // Override isOnline if timestamp is stale (older than 1 minute)
  const effectiveIsOnline = isStale ? false : isOnline;
  const rpm = machineState?.RPM || 0;
  const d2 = machineState?.D2 ?? 0;
  const d5 = machineState?.D5 ?? 0;

  return (
    <div className="space-y-1">
      {/* Only show WebSocket connection status - machine operational status is shown in main badge above */}
      {/*<div className="flex items-center">*/}
      {/*  {getConnectionStatus()}*/}
      {/*</div>*/}
      
      {showDetails && (
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
          <div className="flex items-center space-x-1">
            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reason, isOnline, isStale)}`}>
              {getStatusIcon(reason, effectiveIsOnline)}
              <span className="ml-1">{getStatusText(reason, isOnline, isStale)}</span>
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Activity className="h-3 w-3" />
            <span>RPM: {rpm.toFixed(0)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Power className="h-3 w-3" />
            <span>D2: {d2}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Power className="h-3 w-3" />
            <span>D5: {d5}</span>
          </div>
          <div className="text-xs text-gray-400">
            Updated: {machineState?.timestamp ? new Date(machineState.timestamp).toLocaleTimeString() : 'Never'}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealtimeMachineStatus;
